/**
 * Composant pour afficher un graphique en ligne.
 *
 * Ce composant reçoit les données du graphique via la propriété `chartData` et
 * des options pour le graphique via la propriété `chartOptions`. Les options
 * par défaut sont définies pour correspondre au design OpSolver.
 *
 * @prop {Object} chartData - Les données du graphique.
 * @prop {Object} chartOptions - Les options pour le graphique.
 * @prop {Number} height - La hauteur du graphique (par défaut 300px).
 * @prop {Number} width - La largeur du graphique (par défaut null, qui signifie
 *                         que la largeur est automatiquement déterminée).
 */
<script setup>
import { defineProps, defineEmits, onMounted, watch, ref } from 'vue';
import { Line } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

// Enregistrement des composants Chart.js nécessaires
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Définition des props
const props = defineProps({
  chartData: {
    type: Object,
    required: true
  },
  chartOptions: {
    type: Object,
    default: () => ({})
  },
  height: {
    type: Number,
    default: 300
  },
  width: {
    type: Number,
    default: null
  }
});

// Référence au composant Chart
const chartRef = ref(null);

// Options par défaut pour le graphique
const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: {
          family: "'Poppins', sans-serif",
          size: 12
        },
        usePointStyle: true,
        padding: 20
      }
    },
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      titleColor: '#333',
      bodyColor: '#666',
      bodyFont: {
        family: "'Poppins', sans-serif"
      },
      titleFont: {
        family: "'Poppins', sans-serif",
        weight: 'bold'
      },
      borderColor: '#ddd',
      borderWidth: 1,
      padding: 10,
      boxPadding: 5,
      usePointStyle: true,
      callbacks: {
        labelTextColor: () => '#333'
      }
    }
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(0, 0, 0, 0.05)'
      },
      ticks: {
        font: {
          family: "'Poppins', sans-serif"
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)'
      },
      ticks: {
        font: {
          family: "'Poppins', sans-serif"
        }
      }
    }
  }
};

// Fusion des options par défaut avec les options passées en props
const chartOptions = {
  ...defaultOptions,
  ...props.chartOptions
};

// Surveiller les changements de données pour mettre à jour le graphique
watch(() => props.chartData, (newData) => {
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.data = newData;
    chartRef.value.chart.update();
  }
}, { deep: true });
</script>

<template>
  <div class="chart-wrapper" :style="{ height: `${height}px`, width: width ? `${width}px` : '100%' }">
    <Line 
      ref="chartRef"
      :data="chartData"
      :options="chartOptions"
    />
  </div>
</template>

<style scoped>
.chart-wrapper {
  position: relative;
  margin: auto;
}
</style>
